import {
  differenceInMonths,
  differenceInYears,
  isValid,
  parseISO,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

export const AGE_UNAVAILABLE = 'Age unavailable';

const YOUNG_CHILD_YEAR_LIMIT = 6;

export function studioCalendarDay(
  timeZone: string | null | undefined,
  now: Date = new Date(),
): string {
  const zone = timeZone?.trim() || 'UTC';
  try {
    return formatInTimeZone(now, zone, 'yyyy-MM-dd');
  } catch {
    return formatInTimeZone(now, 'UTC', 'yyyy-MM-dd');
  }
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

/**
 * Age on the studio's local calendar day.
 * Months are included for children under 6. Missing or future dates stay unlabeled.
 */
export function formatStudentProfileAge(
  dob: string | null | undefined,
  timeZone: string | null | undefined,
  now: Date = new Date(),
): string {
  const birthDay = dob?.trim().slice(0, 10);
  if (!birthDay) {
    return AGE_UNAVAILABLE;
  }

  const birth = parseISO(birthDay);
  const asOf = parseISO(studioCalendarDay(timeZone, now));
  if (!isValid(birth) || !isValid(asOf)) {
    return AGE_UNAVAILABLE;
  }

  const totalMonths = differenceInMonths(asOf, birth);
  if (totalMonths < 0) {
    return AGE_UNAVAILABLE;
  }

  const years = differenceInYears(asOf, birth);
  const months = totalMonths % 12;
  if (years >= YOUNG_CHILD_YEAR_LIMIT) {
    return plural(years, 'year');
  }
  if (years === 0) {
    return plural(months, 'month');
  }
  if (months === 0) {
    return plural(years, 'year');
  }
  return `${plural(years, 'year')} ${plural(months, 'month')}`;
}
